// MCHD Room Scheduler - Main App Component
// Main React application component with authentication

import { DatesProvider } from '@mantine/dates';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import LoginPage from './pages/LoginPage';
import { DashboardLayout } from './layouts/DashboardLayout';
import { LoadingScreen, ErrorLoadingScreen } from './components/LoadingScreen';
import { AuthErrorBoundary } from './components/ErrorBoundary';

// Import dashboard components
import UserManagement from './features/admin/users/UserManagement';
import { SessionManagement } from './features/admin/sessions/SessionManagement';
import { SecurityDashboard } from './features/admin/security/SecurityDashboard';
import { RoomManagement } from './features/admin/rooms/RoomManagement';
import { BuildingManagement } from './features/admin/buildings/BuildingManagement';
import { HolidayManagement } from './features/admin/holidays/HolidayManagement';
import BlockManagement from './features/admin/blocks/BlockManagement';
import { SessionTimeManagement } from './features/admin/session-times/SessionTimeManagement';
import { AuditLogsReports } from './features/admin/audit-logs/AuditLogsReports';
import { ReportsAnalytics } from './features/admin/reports/ReportsAnalytics';

// Import coordinator components
import EnhancedPlannerPage from './pages/coordinator/EnhancedPlannerPage';
import CoordinatorDashboard from './pages/coordinator/CoordinatorDashboard';
import CoordinatorUserManagement from './pages/coordinator/CoordinatorUserManagement';
import CoordinatorRoomManagement from './pages/coordinator/CoordinatorRoomManagement';
import CoordinatorAssignments from './pages/coordinator/CoordinatorAssignments';
import CoordinatorBlockManagement from './pages/coordinator/CoordinatorBlockManagement';
import RoomDetailPage from './pages/coordinator/RoomDetailPage';

// Import doctor components
import DoctorDashboard from './pages/doctor/DoctorDashboard';
import DoctorSchedulePage from './pages/doctor/DoctorSchedulePage';
import DoctorProfilePage from './pages/doctor/DoctorProfilePage';

/**
 * Check if an error is critical and should show error screen
 * @param error - Error message
 * @returns boolean - true if critical error
 */
function isCriticalError(error: string): boolean {
  const criticalErrors = [
    'INTERNAL_SERVER_ERROR',
    'DATABASE_ERROR',
    'SYSTEM_ERROR',
    'NETWORK_ERROR',
    'CONNECTION_ERROR'
  ];
  
  return criticalErrors.some(criticalError => 
    error.toUpperCase().includes(criticalError)
  );
}

function AppContent(): JSX.Element {
  const { user, login, logout, isLoading, error, isAuthenticated, isInitializing } = useAuth();

  // Show loading screen during initial authentication check
  if (isInitializing) {
    return <LoadingScreen message="Initializing authentication..." />;
  }

  // Show error screen only for critical system errors (not login validation errors)
  if (error && !isLoading && !isAuthenticated && isCriticalError(error)) {
    return (
      <ErrorLoadingScreen 
        message={error}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // If user is not authenticated, show login page
  if (!isAuthenticated || !user) {
    return <LoginPage onLogin={login} isLoading={isLoading} error={error} />;
  }

  // If user is authenticated, show dashboard with routing
  return (
    <DashboardLayout user={user} onLogout={logout}>
      <Routes>
        {/* Admin Routes */}
        {user.role === 'ADMIN' && (
          <>
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/buildings" element={<BuildingManagement />} />
            <Route path="/admin/rooms" element={<RoomManagement />} />
            <Route path="/admin/session-times" element={<SessionTimeManagement />} />
            <Route path="/admin/blocks" element={<BlockManagement />} />
            <Route path="/admin/holidays" element={<HolidayManagement />} />
            <Route path="/admin/sessions" element={<SessionManagement />} />
            <Route path="/admin/security" element={<SecurityDashboard />} />
            <Route path="/admin/audit-logs" element={<AuditLogsReports />} />
            <Route path="/admin/reports" element={<ReportsAnalytics />} />
          </>
        )}
        
        {/* Coordinator Routes */}
        {user.role === 'COORDINATOR' && (
          <>
            <Route path="/coordinator/dashboard" element={<CoordinatorDashboard />} />
            {/* New planner route (replaces /coordinator/scheduling) */}
            <Route path="/planner" element={<EnhancedPlannerPage />} />
            {/* Backward compatibility redirect */}
            <Route path="/coordinator/scheduling" element={<Navigate to="/planner" replace />} />
            {/* Session Times removed - now managed within Building Management flow */}
            <Route path="/coordinator/rooms" element={<CoordinatorRoomManagement />} />
            <Route path="/coordinator/assignments" element={<CoordinatorAssignments />} />
            <Route path="/coordinator/blocks" element={<CoordinatorBlockManagement />} />
        <Route path="/coordinator/users" element={<CoordinatorUserManagement />} />
        <Route path="/coordinator/rooms/:roomId" element={<RoomDetailPage />} />
          </>
        )}
        
        {/* Doctor Routes */}
        {user.role === 'DOCTOR' && (
          <>
            <Route path="/doctor/dashboard" element={<DoctorDashboard />} />
            <Route path="/doctor/schedule" element={<DoctorSchedulePage />} />
            <Route path="/doctor/profile" element={<DoctorProfilePage />} />
          </>
        )}
        
        {/* Default redirect based on role */}
        <Route 
          path="/" 
          element={
            <Navigate 
              to={
                user.role === 'ADMIN' ? '/admin/users' :
                user.role === 'COORDINATOR' ? '/planner' :
                user.role === 'DOCTOR' ? '/doctor/dashboard' :
                '/admin/users'
              } 
              replace 
            />
          } 
        />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </DashboardLayout>
  );
}

function App(): JSX.Element {
  return (
    <AuthErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <DatesProvider settings={{ firstDayOfWeek: 0 }}>
          <AppContent />
        </DatesProvider>
      </BrowserRouter>
    </AuthErrorBoundary>
  );
}

export default App;
